/* eslint-disable react/prop-types */
// =============================================================================
// Decision History Dialog
// -----------------------------------------------------------------------------
// Read-only modal showing every `final_action` transition for a single booking.
// Used for auditability, debugging, and model-performance evaluation.
//
// Backed by `GET /api/admin/portfolio-decision-log?booking_id=…`, populated by
// the fire-and-forget writer in `server.js#persistDecisionLogChanges`.
// =============================================================================

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowRight,
  Brain,
  CheckCircle2,
  ClipboardList,
  Clock,
  Droplets,
  Gauge,
  MinusCircle,
  Pause,
  RefreshCw,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  XCircle,
} from 'lucide-react';

// Action display tables — keep visually in sync with the action pills used
// elsewhere so the timeline reads at a glance.
const FINAL_LABEL = {
  BOOK_NOW:           'Book Now',
  PAYMENT_REQUIRED:   'Payment Required',
  HOLD_FOR_LIQUIDITY: 'Hold · Liquidity',
  WATCH_CLOSELY:      'Watch Closely',
  MANUAL_REVIEW:      'Manual Review',
};

const FINAL_PILL = {
  BOOK_NOW:           'text-emerald-100 bg-emerald-950/55 border-emerald-400/35',
  PAYMENT_REQUIRED:   'text-rose-100 bg-rose-950/55 border-rose-400/35',
  HOLD_FOR_LIQUIDITY: 'text-brand-100 bg-brand-950/55 border-brand-400/35',
  WATCH_CLOSELY:      'text-brand-100 bg-brand-950/55 border-brand-400/30',
  MANUAL_REVIEW:      'text-brand-100 bg-brand-950/55 border-brand-400/35',
};

const PORTFOLIO_AI_LABEL = {
  APPROVED_TO_BOOK:    'Approved',
  HOLD_FOR_LIQUIDITY:  'Hold · Liquidity',
  PAYMENT_REQUIRED:    'Payment Required',
  MANUAL_REVIEW:       'Manual Review',
};

const PORTFOLIO_AI_PILL = {
  APPROVED_TO_BOOK:    'text-emerald-200 bg-emerald-950/45 border-emerald-400/25',
  HOLD_FOR_LIQUIDITY:  'text-brand-100 bg-brand-950/40 border-brand-400/20',
  PAYMENT_REQUIRED:    'text-rose-200 bg-rose-950/50 border-rose-400/30',
  MANUAL_REVIEW:       'text-brand-200 bg-brand-950/45 border-brand-400/25',
};

const TRIP_AI_LABEL = {
  BOOK_NOW:              'Book Now',
  WAIT_FOR_NEXT_PAYMENT: 'Wait · Next Pmt',
  HOLD_BOOKING:          'Hold Booking',
  MONITOR:               'Monitor',
};

const RISK_GRADE_PILL = {
  A: 'text-emerald-200 bg-emerald-950/45 border-emerald-400/25',
  B: 'text-emerald-100 bg-emerald-950/35 border-emerald-400/20',
  C: 'text-brand-100 bg-brand-950/40 border-brand-400/20',
  D: 'text-orange-200 bg-orange-950/45 border-orange-400/25',
  F: 'text-rose-200 bg-rose-950/50 border-rose-400/30',
};

const fmt$ = (cents) => {
  if (cents == null) return '—';
  const dollars = Number(cents) / 100;
  if (!Number.isFinite(dollars)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(dollars);
};

const fmtRatio = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return '—'; }
};

const fmtRelative = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
};

const SOURCE_LABEL = {
  risk_review:           'Risk Review',
  portfolio_intelligence: 'Portfolio Intelligence',
};

// Outcome status display. Kept in lockstep with `deriveOutcomeStatus` server-side.
const OUTCOME_STATUS = {
  pending: {
    label: 'Pending',
    pill: 'text-stone-300 bg-zinc-900/55 border-white/10',
    icon: Pause,
    blurb: 'No terminal state yet — still in flight.',
  },
  won: {
    label: 'Won',
    pill: 'text-emerald-100 bg-emerald-950/55 border-emerald-400/35',
    icon: CheckCircle2,
    blurb: 'AI recommendation was financially beneficial.',
  },
  lost: {
    label: 'Lost',
    pill: 'text-rose-100 bg-rose-950/55 border-rose-400/35',
    icon: TrendingDown,
    blurb: 'Decision resulted in a realised loss or bad customer outcome.',
  },
  neutral: {
    label: 'Neutral',
    pill: 'text-brand-100 bg-brand-950/55 border-brand-400/30',
    icon: MinusCircle,
    blurb: 'Booking executed without notable P&L movement.',
  },
};

export default function DecisionHistoryDialog({
  open,
  onClose,
  bookingId,
  bookingReference,
  route,
}) {
  const [entries, setEntries] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [outcomeStatus, setOutcomeStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!open || !bookingId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Not authenticated');
        const auth = { Authorization: `Bearer ${session.access_token}` };
        // Decision log + outcome in parallel; one network round trip wall-clock.
        const [logResp, outResp] = await Promise.all([
          fetch(`/api/admin/portfolio-decision-log?booking_id=${encodeURIComponent(bookingId)}&limit=50`, { headers: auth }),
          fetch(`/api/admin/portfolio-decision-outcome?booking_id=${encodeURIComponent(bookingId)}`,         { headers: auth }),
        ]);
        if (!logResp.ok) throw new Error((await logResp.text()) || `HTTP ${logResp.status}`);
        const logJson = await logResp.json();
        let outJson = { outcome: null, outcome_status: 'pending' };
        if (outResp.ok) outJson = await outResp.json();
        if (!cancelled) {
          setEntries(logJson.entries || []);
          setOutcome(outJson.outcome || null);
          setOutcomeStatus(outJson.outcome_status || 'pending');
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, bookingId]);

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0a0a0d] border border-white/10 text-stone-200 max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-white font-light text-lg flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-brand-200/70" strokeWidth={1.25} />
            Decision History
          </DialogTitle>
          <DialogDescription className="text-stone-400 font-normal text-sm">
            Read-only audit trail of every <code className="text-stone-300">final_action</code> change for {bookingReference || route || 'this trip'}.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center">
            <RefreshCw className="w-5 h-5 text-brand-200/40 animate-spin mx-auto mb-3" />
            <p className="text-stone-400 text-sm font-normal">Loading decision log…</p>
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-rose-400/25 bg-rose-950/35 text-rose-200/90 text-sm font-normal px-3 py-2.5 flex items-center gap-2">
            <XCircle className="w-4 h-4 shrink-0" strokeWidth={1.25} />
            {error}
          </div>
        )}

        {!loading && !error && (
          <OutcomeCard outcome={outcome} status={outcomeStatus} />
        )}

        {!loading && !error && entries && entries.length === 0 && (
          <div className="rounded-xl border border-white/[0.06] bg-zinc-950/60 px-3 py-6 text-center">
            <Sparkles className="w-4 h-4 text-brand-200/40 mx-auto mb-2" strokeWidth={1.25} />
            <p className="text-stone-300 text-sm font-normal">No decision changes recorded yet.</p>
            <p className="text-stone-600 text-xs font-normal mt-1">
              Entries are written automatically the next time <code className="text-stone-400">final_action</code> changes for this trip.
            </p>
          </div>
        )}

        {!loading && !error && entries && entries.length > 0 && (
          <ol className="mt-2 space-y-2.5">
            {entries.map((e, i) => (
              <DecisionEntry key={e.id} entry={e} isMostRecent={i === 0} />
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DecisionEntry({ entry, isMostRecent }) {
  const prev = entry.previous_final_action;
  const next = entry.new_final_action;
  const prevPill = prev ? (FINAL_PILL[prev] || FINAL_PILL.MANUAL_REVIEW) : 'text-stone-400 bg-zinc-900/55 border-white/10';
  const nextPill = FINAL_PILL[next] || FINAL_PILL.MANUAL_REVIEW;
  const portfolioPill = entry.portfolio_ai_action
    ? (PORTFOLIO_AI_PILL[entry.portfolio_ai_action] || PORTFOLIO_AI_PILL.MANUAL_REVIEW)
    : null;
  const gradePill = entry.customer_risk_grade ? RISK_GRADE_PILL[entry.customer_risk_grade] : null;

  return (
    <li className={`rounded-xl border ${isMostRecent ? 'border-brand-400/30 bg-brand-950/[0.08]' : 'border-white/[0.06] bg-zinc-950/60'} px-3 py-2.5`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="w-3 h-3 text-stone-500 shrink-0" strokeWidth={1.25} />
          <span className="text-xs text-stone-300 font-normal tabular-nums">{fmtWhen(entry.created_at)}</span>
          <span className="text-[10px] text-stone-600 font-normal">{fmtRelative(entry.created_at)}</span>
          {entry.source && (
            <span className="text-[10px] uppercase tracking-wider text-stone-500 border border-white/[0.06] rounded-full px-1.5 py-0.5">
              {SOURCE_LABEL[entry.source] || entry.source}
            </span>
          )}
        </div>
        {isMostRecent && (
          <span className="text-[10px] uppercase tracking-wider text-brand-200/80 border border-brand-400/25 rounded-full px-1.5 py-0.5">
            Current
          </span>
        )}
      </div>

      {/* Transition */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        {prev ? (
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${prevPill} whitespace-nowrap`}>
            {FINAL_LABEL[prev] || prev}
          </span>
        ) : (
          <span className="text-[10px] font-normal text-stone-500 border border-dashed border-white/15 rounded-full px-2 py-0.5">
            initial
          </span>
        )}
        <ArrowRight className="w-3 h-3 text-stone-500" strokeWidth={1.5} />
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${nextPill} whitespace-nowrap`}>
          {FINAL_LABEL[next] || next}
        </span>
        {entry.trip_ai_action && (
          <span className="ml-2 text-[10px] text-stone-500 font-normal">
            <span className="uppercase tracking-wider text-stone-600 mr-1">Trip AI</span>
            {TRIP_AI_LABEL[entry.trip_ai_action] || entry.trip_ai_action}
          </span>
        )}
        {entry.portfolio_ai_action && portfolioPill && (
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${portfolioPill}`}>
            <span className="text-white/40 uppercase tracking-wider text-[9px] mr-1">Portfolio</span>
            {PORTFOLIO_AI_LABEL[entry.portfolio_ai_action] || entry.portfolio_ai_action}
          </span>
        )}
      </div>

      {entry.final_action_reason && (
        <p className="text-xs text-stone-300 font-normal leading-relaxed italic mb-2">
          {entry.final_action_reason}
        </p>
      )}

      {/* Context strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-2.5 gap-y-1.5 text-[11px]">
        <Ctx icon={Gauge}      label="Health"     value={entry.portfolio_health_score ?? '—'} />
        <Ctx icon={Droplets}   label="LCR · 14d"  value={fmtRatio(entry.liquidity_coverage_ratio_14d)} />
        <Ctx icon={Clock}      label="Compress"   value={entry.days_until_liquidity_compression == null ? '—' : `${entry.days_until_liquidity_compression}d`} />
        <Ctx icon={Brain}      label="Cash"       value={fmt$(entry.cash_on_hand_cents)} />
        <Ctx icon={Brain}      label="Reserve gap" value={fmt$(entry.reserve_gap_cents)} danger={(entry.reserve_gap_cents || 0) > 0} />
        <Ctx
          icon={Brain}
          label="Ticket cost"
          value={fmt$(entry.estimated_ticket_cost_cents)}
          sub={entry.current_live_fare_cents != null ? `live ${fmt$(entry.current_live_fare_cents)}` : null}
        />
      </div>

      {(entry.customer_risk_grade || entry.customer_risk_score != null) && (
        <div className="mt-1.5 flex items-center gap-2 text-[10px]">
          {entry.customer_risk_grade && (
            <span className={`px-1.5 py-0.5 rounded-full font-medium border ${gradePill || 'text-stone-400 bg-zinc-900/55 border-white/10'}`}>
              Risk {entry.customer_risk_grade}
            </span>
          )}
          {entry.customer_risk_score != null && (
            <span className="text-stone-500 font-normal tabular-nums">score {entry.customer_risk_score}</span>
          )}
        </div>
      )}
    </li>
  );
}

// ─── Outcome (analytics-only) ────────────────────────────────────────────────
//
// Renders the ground-truth row from `portfolio_decision_outcomes` plus the
// derived status pill (pending / won / lost / neutral).  Pure read; the
// schema is populated by the server-side upserter on each Risk Review or
// Portfolio Intelligence run.

function OutcomeCard({ outcome, status }) {
  const meta = OUTCOME_STATUS[status] || OUTCOME_STATUS.pending;
  const Icon = meta.icon;
  const ticketed = Boolean(outcome?.booked_at);
  const savings = Number(outcome?.realized_savings_cents || 0);
  const loss    = Number(outcome?.realized_loss_cents    || 0);
  const net     = savings - loss;
  const NetIcon = net > 0 ? TrendingUp : (net < 0 ? TrendingDown : MinusCircle);

  return (
    <section className="mt-3 rounded-xl border border-white/[0.08] bg-gradient-to-br from-zinc-950/80 to-black/40 px-3.5 py-3">
      <header className="flex items-center justify-between gap-2 flex-wrap mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Target className="w-4 h-4 text-brand-200/60" strokeWidth={1.25} />
          <h3 className="text-white/90 font-normal text-sm tracking-wide">Outcome</h3>
          <span className="text-[10px] text-stone-600 font-normal uppercase tracking-[0.2em]">analytics only</span>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${meta.pill}`}>
          <Icon className="w-3 h-3" strokeWidth={1.5} />
          {meta.label}
        </span>
      </header>

      <p className="text-xs text-stone-400 font-normal italic mb-3">{meta.blurb}</p>

      {!outcome && (
        <p className="text-xs text-stone-400 font-normal">
          No outcome record yet — one will be seeded the next time this booking is assessed.
        </p>
      )}

      {outcome && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-2.5 gap-y-2 text-[11px]">
            <Ctx icon={Brain} label="Fare @ decision"  value={fmt$(outcome.fare_at_decision_cents)} />
            <Ctx icon={Brain} label="Fare when booked" value={fmt$(outcome.fare_when_booked_cents)} sub={ticketed ? null : 'pending'} />
            <Ctx icon={Brain} label="Cost @ decision"  value={fmt$(outcome.estimated_ticket_cost_at_decision_cents)} />
            <Ctx icon={Brain} label="Actual cost"      value={fmt$(outcome.actual_ticket_cost_cents)} sub={ticketed ? null : 'pending'} />
            <Ctx
              icon={Clock}
              label="Days waited"
              value={outcome.days_waited_after_decision == null ? '—' : `${Number(outcome.days_waited_after_decision).toFixed(1)}d`}
              sub={ticketed ? null : 'in flight'}
            />
            <Ctx
              icon={TrendingUp}
              label="Realised savings"
              value={savings > 0 ? fmt$(savings) : (ticketed ? '$0' : '—')}
            />
            <Ctx
              icon={TrendingDown}
              label="Realised loss"
              value={loss > 0 ? fmt$(loss) : (ticketed ? '$0' : '—')}
              danger={loss > 0}
            />
            <Ctx
              icon={NetIcon}
              label="Net"
              value={ticketed ? (net === 0 ? '$0' : `${net > 0 ? '+' : '−'}${fmt$(Math.abs(net))}`) : '—'}
              danger={net < 0}
            />
          </div>

          {/* Customer & override flags */}
          <div className="mt-3 flex items-center gap-1.5 flex-wrap text-[10px]">
            <FlagPill on={outcome.customer_completed_payments} label="Paid in full" tone="emerald" />
            <FlagPill on={outcome.customer_defaulted}          label="Defaulted"    tone="rose"    />
            <FlagPill on={outcome.chargeback_occurred}         label="Chargeback"   tone="rose"    />
            <FlagPill on={outcome.manual_override}             label="Manual override" tone="violet" />
            {outcome.booked_at && (
              <span className="text-[10px] text-stone-600 font-normal tabular-nums">
                Booked {fmtWhen(outcome.booked_at)}
              </span>
            )}
            {outcome.completed_at && (
              <span className="text-[10px] text-stone-600 font-normal tabular-nums">
                Completed {fmtWhen(outcome.completed_at)}
              </span>
            )}
          </div>

          {outcome.override_reason && (
            <p className="mt-2 text-[11px] text-brand-200/70 font-normal italic">
              Override reason: {outcome.override_reason}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function FlagPill({ on, label, tone }) {
  const palette = {
    emerald: on ? 'text-emerald-100 bg-emerald-950/55 border-emerald-400/35' : 'text-stone-600 bg-zinc-950/40 border-white/5',
    rose:    on ? 'text-rose-100 bg-rose-950/55 border-rose-400/35'          : 'text-stone-600 bg-zinc-950/40 border-white/5',
    violet:  on ? 'text-brand-100 bg-brand-950/55 border-brand-400/35'    : 'text-stone-600 bg-zinc-950/40 border-white/5',
  }[tone] || 'text-stone-500 bg-zinc-950/40 border-white/5';
  return (
    <span className={`px-1.5 py-0.5 rounded-full border ${palette} ${!on ? 'line-through decoration-stone-700' : ''}`}>
      {label}
    </span>
  );
}

function Ctx({ icon: Icon, label, value, sub, danger }) {
  return (
    <div className="flex flex-col">
      <span className="flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] text-stone-600 font-medium">
        {Icon && <Icon className="w-2.5 h-2.5" strokeWidth={1.5} />}
        {label}
      </span>
      <span className={`tabular-nums font-normal ${danger ? 'text-rose-300/90' : 'text-stone-200'}`}>{value}</span>
      {sub && <span className="text-[10px] text-stone-600 font-normal">{sub}</span>}
    </div>
  );
}
